import axios from "axios";
import myaxios from "lib/myAxios";
import {
  getUserSubscriptions,
  changeMobile,
  resendMobileOtp as resendMobileOtpUrl,
  verifyMobile as verifyMobileUrl,
} from "lib/common/endpoints/endUser";
import { Error } from "lib/common/types/ErrorResponse";
import { User } from "lib/common/types/user";
import { useRouter } from "next/router";
import { useState } from "react";
import useSWR from "swr";
import { useAppDispatch } from "../../redux/hooks";
import {
  login as login_,
  onBoardUser,
  updateOnboardStatus,
  updateSubscribeStatus,
} from "../../redux/reducers/userSlice";
import { host } from "../../utils/constants";
import parseJwt from "../../utils/helper/jwtParser";
import { setAccessToken, setRefreshToken } from "../accessToken";
import useGetMe from "./useGetMe";
import useGetOnboardStatus from "./useGetOnboardStatus";
import useLocalStorage from "./useLocalStorage";
import { getTurnstileToken } from "utils/turnstile";
import { getOrCreateDeviceToken } from "utils/deviceToken";
import { getSafeRedirect } from "utils/helper/safeRedirect";

export interface DeviceSession {
  sessionId: string;
  deviceLabel: string;
  ip: string;
  createdAt: number;
  lastActiveAt: number;
  current: boolean;
}

interface DeviceLimitState {
  devices: DeviceSession[];
  pendingData: Record<string, string>;
}

interface OtpRequiredState {
  mobile: string;
  password: string;
}

interface AddMobileState {
  step: "mobile" | "otp";
  mobile?: string;
}

function useLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);
  const [deviceLimit, setDeviceLimit] = useState<DeviceLimitState | null>(null);
  const [otpRequired, setOtpRequired] = useState<OtpRequiredState | null>(null);
  const [addMobileRequired, setAddMobileRequired] = useState<AddMobileState | null>(null);
  const dispatch = useAppDispatch();
  const { fetchUser } = useGetMe();
  const { fetchOnboardStatus } = useGetOnboardStatus();
  const { setValue: setUser } = useLocalStorage("user");
  const { data: subscriptions } = useSWR(success ? getUserSubscriptions : null);
  const router = useRouter();
  // Honour /login?next=... so a guest stopped at a sign-in gate (e.g. a mock
  // test lobby) lands back where they were headed instead of the dashboard.
  const afterLogin = getSafeRedirect(router.query.next);

  /** Shared tail of a completed login -- used by both a normal login
   * response and a successful OTP-device-verification response, since
   * the backend returns the exact same shape for both (see _finish_login
   * in login_routes.py). */
  const _finishLogin = (respData: Record<string, any>) => {
    setDeviceLimit(null);
    setOtpRequired(null);
    setAddMobileRequired(null);
    setAccessToken(respData.accessToken);
    setRefreshToken(respData.refreshToken);

    fetchOnboardStatus()
      .then((onBoardingStatus) => {
        dispatch(updateOnboardStatus({ onBoardingStatus }));
      })
      .catch(() => undefined);

    fetchUser().then((fetchedUser) => {
      setUser(fetchedUser);
      dispatch(
        login_({
          user: fetchedUser as User,
          isAdmin: parseJwt(respData.accessToken).isAdminUser,
          isSuperAdmin: parseJwt(respData.accessToken).isSuperAdminUser,
        }),
      );
      if (subscriptions?.data) {
        dispatch(updateSubscribeStatus({ subscriptionsStatus: subscriptions.data }));
      }
    });

    dispatch(
      onBoardUser({
        user: { userData: { username: respData?.username, mobile: respData?.mobile } },
        onBoardingStatus: respData.onBoardingStatus,
      }),
    );

    if (respData.onBoardingToken) {
      localStorage.setItem("registerToken", respData.onBoardingToken);
    }

    if (!respData.mobile) {
      // Some accounts (older/legacy signups) genuinely have no mobile
      // number on file. Tokens are already set above, so the user is
      // logged in -- prompt them to add one instead of silently sending
      // them to the dashboard with an incomplete profile.
      setAddMobileRequired({ step: "mobile" });
      return;
    }

    if (!respData.onBoardingStatus.mobileVerified) {
      router.push("/register/verify-otp");
    } else {
      router.push(afterLogin);
    }
  };

  const _doLogin = async (data: Record<string, string>) => {
    setLoading(true);
    setError(null);

    let captchaToken: string;
    try {
      captchaToken = await getTurnstileToken();
    } catch (err) {
      // getTurnstileToken() rejecting (widget failure, or its own 20s
      // timeout) was previously unhandled here -- since this await sat
      // outside the axios .catch()/.finally() chain below, the thrown
      // error skipped straight past both, leaving `loading` stuck true
      // and the submit button spinning forever regardless of whether
      // Turnstile itself ever recovered.
      const message =
        err instanceof globalThis.Error
          ? err.message
          : "Could not verify you're human right now. Please try again.";
      setError({ message });
      setLoading(false);
      return;
    }

    const deviceToken = getOrCreateDeviceToken();

    axios
      .post(host + `/auth/login`, { ...data, captchaToken, deviceToken })
      .then(({ data: { data: respData, message, success } }) => {
        setSuccess(success);
        if (success) {
          if (respData.otpRequired) {
            // Zero-trust super-admin login: this device hasn't cleared
            // the OTP challenge yet -- no tokens issued, nothing to
            // store. This response also has success:true (same envelope
            // as a real login) but no accessToken/mobile/anything else,
            // so it was previously falling straight into the normal
            // "finished login" branch below, which then saw a missing
            // `mobile` field and redirected to a mobile-linking page
            // that doesn't exist in this app at all. Show the OTP step
            // instead.
            setOtpRequired({ mobile: data.mobile, password: data.password });
            return;
          }
          _finishLogin(respData);
        } else if (Array.isArray(respData?.devices) && respData.devices.length > 0) {
          // Backend returns 200+success:false with a devices array when the
          // account is at the device cap — show the picker modal.
          setDeviceLimit({ devices: respData.devices, pendingData: data });
        } else {
          setError({ message });
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        const body = err?.response?.data;

        if (status === 409) {
          // Fallback: some deploys may use 409 instead of 200+success:false.
          setDeviceLimit({
            devices: body?.data?.devices ?? [],
            pendingData: data,
          });
        } else if (status === 429) {
          setError({
            message:
              body?.message ??
              "Too many device changes recently. Please wait before switching devices again, or contact support.",
          });
        } else {
          setError(body);
        }
      })
      .finally(() => setLoading(false));
  };

  /** Original entry-point used by the login form */
  const login = (_work: string, data: Record<string, string>) => _doLogin(data);

  /** Called when the user picks a device to evict from the 409 picker */
  const loginWithDeviceSwap = (logoutSessionId: string) => {
    if (!deviceLimit) return;
    _doLogin({ ...deviceLimit.pendingData, logoutSessionId });
  };

  const dismissDeviceLimit = () => setDeviceLimit(null);

  /** Second step of zero-trust super-admin login, called after `login`
   * puts `otpRequired` in state. Re-sends the password alongside the OTP
   * (see LoginVerifyDeviceOtp in login_routes.py -- the OTP alone isn't
   * treated as sufficient on its own) and the same device token so the
   * backend marks this device trusted going forward. */
  const verifyDeviceOtp = (otpValue: string) => {
    if (!otpRequired) return;
    setLoading(true);
    setError(null);

    axios
      .post(host + `/auth/login/verify_device`, {
        mobile: otpRequired.mobile,
        password: otpRequired.password,
        otpValue,
        deviceToken: getOrCreateDeviceToken(),
      })
      .then(({ data: { data: respData, message, success } }) => {
        setSuccess(success);
        if (success) {
          _finishLogin(respData);
        } else {
          setError({ message });
        }
      })
      .catch((err) => {
        setError(err?.response?.data ?? { message: "Failed to verify OTP" });
      })
      .finally(() => setLoading(false));
  };

  const cancelOtpStep = () => setOtpRequired(null);

  /** Submits the phone number for an account that logged in with none on
   * file. Uses the authenticated `myaxios` instance since this hits a
   * @jwt_required() endpoint -- _finishLogin has already set the tokens
   * by the time addMobileRequired can be true. */
  const requestMobile = (mobile: string) => {
    setLoading(true);
    setError(null);

    myaxios
      .put(changeMobile, { changeMobileRequest: mobile })
      .then(() => {
        setAddMobileRequired({ step: "otp", mobile });
      })
      .catch((err) => {
        setError(err?.response?.data ?? { message: "Failed to request mobile number change" });
      })
      .finally(() => setLoading(false));
  };

  const resendMobileOtp = () => {
    if (!addMobileRequired) return;
    setLoading(true);
    setError(null);

    myaxios
      .put(resendMobileOtpUrl)
      .catch((err) => {
        setError(err?.response?.data ?? { message: "Failed to resend OTP" });
      })
      .finally(() => setLoading(false));
  };

  const verifyMobile = (otpValue: string) => {
    if (!addMobileRequired) return;
    setLoading(true);
    setError(null);

    myaxios
      .put(verifyMobileUrl, { otpValue })
      .then(() => {
        setAddMobileRequired(null);
        router.push(afterLogin);
      })
      .catch((err) => {
        setError(err?.response?.data ?? { message: "Failed to verify mobile number" });
      })
      .finally(() => setLoading(false));
  };

  const cancelAddMobile = () => {
    setAddMobileRequired(null);
    router.push(afterLogin);
  };

  return {
    login,
    loginWithDeviceSwap,
    dismissDeviceLimit,
    deviceLimit,
    verifyDeviceOtp,
    cancelOtpStep,
    otpRequired: otpRequired !== null,
    addMobileRequired,
    requestMobile,
    resendMobileOtp,
    verifyMobile,
    cancelAddMobile,
    loading,
    error,
    success,
  };
}

export default useLogin;
